import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Order } from '../../../store/order/api/interface';
import { RecipientDetailsEdit } from '../../../client/services';
import { toastHandler } from '../../../utils/toastHandler';

interface RecipientDetailsEditDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order | null | undefined;
  onRecipientUpdate: (updatedOrder: Order) => void;
}

interface RecipientDetailsData {
  name: string;
  phone: string;
}

const RecipientDetailsEditDialog: React.FC<RecipientDetailsEditDialogProps> = ({
  open,
  onClose,
  order,
  onRecipientUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<RecipientDetailsData>({
    name: '',
    phone: '',
  });

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open && order) {
      const recipientInfo = order.recipientInfo as any || {};
      
      setFormData({
        name: recipientInfo.name || '',
        phone: recipientInfo.phone || '',
      });
      setError(null);
    }
  }, [open, order]);

  const handleInputChange = (field: keyof RecipientDetailsData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Recipient name is required');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Recipient phone number is required');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !order) return;

    setLoading(true);
    setError(null);

    try {
      // Prepare recipient info
      const recipientInfo = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
      };

      // Use the new recipient details edit API
      const updateData = {
        orderId: order.id,
        recipientInfo: recipientInfo,
      };

      console.log('Updating recipient details:', updateData);

      const response = await RecipientDetailsEdit.create(updateData);
      
      if (response && response.success) {
        // Update the order with new data
        const updatedOrder = {
          ...order,
          recipientInfo,
        };
        
        onRecipientUpdate(updatedOrder);
        onClose();
        
        // Show success message
        toastHandler({
          message: response.message || 'Recipient details updated successfully',
          type: 'success',
        });
      }
    } catch (error: any) {
      console.error('Error updating recipient details:', error);
      setError(error.message || 'Failed to update recipient details');
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Edit Recipient Details</Typography>
        <Typography variant="body2" color="text.secondary">
          Update the recipient information for this gift order
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update the recipient's name and phone number for this gift order.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Recipient Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter recipient's full name"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter recipient's phone number"
              />
            </Grid>
          </Grid>

          {/* Preview Section */}
          {(formData.name || formData.phone) && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Preview:
              </Typography>
              <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, bgcolor: 'white' }}>
                {formData.name && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {formData.name}
                  </Typography>
                )}
                {formData.phone && (
                  <Typography variant="body2">
                    <strong>Phone:</strong> {formData.phone}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Details'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipientDetailsEditDialog;

